import type { DataSource, PostDataRequest } from "@/__main__/get-data.mjs";
import { appURLs } from "@/app/constants.mjs";
import type {
  FactionBE,
  GameMode,
  LeaderboardMetricsEnum,
} from "@/game-eft/constants/constants.mjs";
import * as GraphQLTemplates from "@/game-eft/graphql-templates.mjs";
import type {
  CreateMatchInput,
  CreatePlayerSeasonStatInput,
  UpdatePlayerSeasonStatInput,
} from "@/game-eft/models/graphql.mjs";
import { getLatestMapVersion } from "@/game-eft/utils/map-data.mjs";
import { postGQL, queryGQL } from "@/util/gql-formatters.mjs";

export function getWeaponsImages() {
  return `${appURLs.CDN_PLAIN}/eft/json/weaponsv2.json`;
}

export function getMapsImages() {
  return `${appURLs.CDN_PLAIN}/eft/json/mapsv3.json`;
}

export function getItemsLocale() {
  return `${appURLs.CDN_PLAIN}/eft/static/locale/en.json`;
}

export function getMapMarkers(mapName: string): DataSource {
  const mapVersion = getLatestMapVersion(mapName);
  return `${appURLs.CDN_PLAIN}/eft/json/markers/${mapName}${mapVersion}.json`;
}

export function createPlayer(variables: {
  player: {
    accountId: string;
    isBot: boolean;
    username: string;
  };
}): PostDataRequest {
  return postGQL(appURLs.EFT, GraphQLTemplates.createPlayer, variables);
}

export function getPlayer(variables: { accountId: string }): DataSource {
  return queryGQL(appURLs.EFT, GraphQLTemplates.getPlayer, variables);
}

export function getMatchlist(variables: {
  accountId: string;
  seasonId?: string;
  offset?: number;
}): DataSource {
  return queryGQL(appURLs.EFT, GraphQLTemplates.getMatchlist, variables);
}

export function getPlayerSeasonStat(variables: {
  accountId: string;
  gameMode: GameMode.ARENA | GameMode.DEFAULT;
  faction: FactionBE;
}): DataSource {
  return queryGQL(appURLs.EFT, GraphQLTemplates.getPlayerSeasonStat, variables);
}

export function createMatch(variables: {
  match: CreateMatchInput;
}): PostDataRequest {
  return postGQL(appURLs.EFT, GraphQLTemplates.createMatch, variables);
}

export function createPlayerSeasonStats(variables: {
  playerSeasonStat: CreatePlayerSeasonStatInput;
}): PostDataRequest {
  return postGQL(
    appURLs.EFT,
    GraphQLTemplates.createPlayerSeasonStat,
    variables,
  );
}

export function updatePlayerSeasonStats(variables: {
  playerSeasonStat: UpdatePlayerSeasonStatInput;
}): PostDataRequest {
  return postGQL(
    appURLs.EFT,
    GraphQLTemplates.updatePlayerSeasonStat,
    variables,
  );
}

export function getMatch(variables: {
  gameId: string;
  gameMode: GameMode.ARENA | GameMode.DEFAULT;
}): DataSource {
  return queryGQL(appURLs.EFT, GraphQLTemplates.getMatch, variables);
}

export function getPlayerWeaponStats(variables: {
  accountId: string;
  gameMode: GameMode.ARENA | GameMode.DEFAULT;
}): DataSource {
  return queryGQL(appURLs.EFT, GraphQLTemplates.getPlayerWeaponStat, variables);
}

export function getPlayerFactionStats(variables: {
  accountId: string;
}): DataSource {
  return queryGQL(
    appURLs.EFT,
    GraphQLTemplates.getPlayerFactionStat,
    variables,
  );
}

export function getPlayerMapStats(variables: {
  accountId: string;
}): DataSource {
  return queryGQL(appURLs.EFT, GraphQLTemplates.getPlayerMapStat, variables);
}

export function getSeasons(): DataSource {
  return queryGQL(appURLs.EFT, GraphQLTemplates.getSeasons);
}

export function getLeaderboardsWeapons(variables: {
  seasonId: string;
  gameMode: string;
  faction: string;
  rank: string;
}): DataSource {
  return queryGQL(
    appURLs.DATALAKE,
    GraphQLTemplates.getLeaderboardsWeapons,
    variables,
  );
}

export function getLeaderboardsMaps(variables: {
  seasonId: string;
  gameMode: string;
  faction: string;
}): DataSource {
  return queryGQL(
    appURLs.DATALAKE,
    GraphQLTemplates.getLeaderboardsMaps,
    variables,
  );
}

export function getLeaderboardsPlayers(variables: {
  seasonId: string;
  gameMode: string;
  faction: string;
  page: string;
  rankingMetric: LeaderboardMetricsEnum;
}): DataSource {
  return queryGQL(
    appURLs.DATALAKE,
    GraphQLTemplates.getLeaderboardsPlayers,
    variables,
  );
}

export function getItems(): DataSource {
  return queryGQL(appURLs.EFT, GraphQLTemplates.getItems);
}

export function getFleaMarketData() {
  return queryGQL(appURLs.DATALAKE, GraphQLTemplates.getFleaMarketData);
}
